import { supabase } from "@/lib/supabase";
import { rateLimit } from "@/lib/utils/rateLimiter";
import useImageModelStore from "@/store/imageModelStore";
import { ChatCompletionService } from "../../../services/openai/chatCompletionService.js";
import { BaseTool } from "./BaseTool.js";

// Create service instance
const chatCompletionService = new ChatCompletionService();

// Upload image to Supabase storage (same as image generation tool)
const uploadImageToStorage = async (base64Data, prompt) => {
  try {
    // Get user ID from auth store
    const { useAuthStore } = await import("@/store/authStore");
    const userId =
      useAuthStore.getState().userId || useAuthStore.getState().user?.id;

    if (!userId) {
      console.error("No user ID available for image upload");
      throw new Error("User not authenticated");
    }

    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: "image/png" });

    const folderUuid = crypto.randomUUID();
    const fileName = `image-edit-${Date.now()}.png`;
    // Organize by user ID: {userId}/{folderUuid}/{fileName}
    const filePath = `${userId}/${folderUuid}/${fileName}`;

    const { data, error } = await supabase.storage
      .from("chat")
      .upload(filePath, blob, {
        contentType: "image/png",
        upsert: false,
        metadata: {
          user_id: userId,
          prompt: prompt,
          generated_at: new Date().toISOString(),
          type: "ai_edited_image",
        },
      });

    if (error) throw error;

    const { data: publicUrlData } = supabase.storage
      .from("chat")
      .getPublicUrl(filePath);

    return {
      url: publicUrlData.publicUrl,
      path: filePath,
      bucket: "chat",
      fileName: fileName,
      folderUuid: folderUuid,
      userId: userId,
      prompt: prompt,
    };
  } catch (error) {
    console.error("Error uploading image to Supabase storage:", error);
    throw error;
  }
};

// Download image from URL and upload to Supabase storage
// Used for non-OpenAI providers that return CDN URLs instead of base64
const downloadAndUploadImage = async (imageUrl, prompt, maxRetries = 3) => {
  const { useAuthStore } = await import("@/store/authStore");
  const userId =
    useAuthStore.getState().userId || useAuthStore.getState().user?.id;

  if (!userId) {
    console.error("No user ID available for image upload");
    throw new Error("User not authenticated");
  }

  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Download image from CDN URL
      console.log(
        `[ImageEditTool] Downloading image from URL (attempt ${attempt + 1}/${maxRetries + 1}):`,
        imageUrl.substring(0, 100)
      );

      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      const contentType = blob.type || "image/png";

      // Generate storage path
      const folderUuid = crypto.randomUUID();
      const extension = contentType.includes("jpeg") ? "jpg" : "png";
      const fileName = `image-edit-${Date.now()}.${extension}`;
      const filePath = `${userId}/${folderUuid}/${fileName}`;

      // Upload to Supabase
      const { data, error } = await supabase.storage
        .from("chat")
        .upload(filePath, blob, {
          contentType,
          upsert: false,
          metadata: {
            user_id: userId,
            prompt: prompt?.substring(0, 500) || "",
            generated_at: new Date().toISOString(),
            type: "ai_edited_image",
            original_url: imageUrl.substring(0, 500),
          },
        });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from("chat")
        .getPublicUrl(filePath);

      if (attempt > 0) {
        console.log(
          `[ImageEditTool] Successfully uploaded to Supabase after ${attempt} retries`
        );
      }

      return {
        url: publicUrlData.publicUrl,
        path: filePath,
        bucket: "chat",
        fileName,
        folderUuid,
        userId,
        prompt,
      };
    } catch (err) {
      lastError = err;
      console.warn(
        `[ImageEditTool] Failed to download/upload image (attempt ${attempt + 1}/${maxRetries + 1}):`,
        err.message
      );

      if (attempt < maxRetries) {
        const delay = 1000 * (attempt + 1); // Exponential backoff
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  console.error(
    `[ImageEditTool] Failed to download/upload image after ${maxRetries + 1} attempts:`,
    lastError
  );
  throw lastError;
};

export class ImageEditTool extends BaseTool {
  constructor() {
    super({
      id: "image_edit",
      name: "Image Edit",
      description:
        "Edit images using AI. Can modify existing images based on text descriptions with optional mask support.",
      category: "Image Generation",
      icon: "🎨",
      runInstructions:
        "Edit images using AI with text prompts and optional masks",
      definition: {
        type: "function",
        function: {
          name: "image_edit",
          description:
            "Edit images using AI. Can modify existing images based on text descriptions with optional mask support.",
          parameters: {
            type: "object",
            properties: {
              prompt: {
                type: "string",
                description:
                  "A detailed description of what changes to make to the image.",
              },
              imageUrls: {
                type: "array",
                items: {
                  type: "string",
                },
                description:
                  "URLs of images to edit or use as references. If a mask is provided, the first image will be the one being edited.",
              },
              maskUrl: {
                type: "string",
                description:
                  "URL of a mask image with transparent areas indicating where the main image should be edited (inpainting). The mask must have an alpha channel and be the same size as the image being edited.",
              },
            },
            required: ["prompt", "imageUrls"],
          },
        },
      },
      execute: () => {},
      type: "function",
      options: {
        rateLimit: {
          maxRequests: 3,
          perMinute: true,
        },
        streamable: false,
        requiresAuth: true,
        timeout: 60000, // 60 seconds for image editing
      },
    });

    // Override the execute method after super()
    this.execute = this.executeImageEdit.bind(this);
  }

  async executeImageEdit(params) {
    try {
      const {
        prompt,
        imageUrls,
        maskUrl,
        model: requestedModelId,
        provider: requestedProvider,
      } = params;

      if (!prompt) {
        throw new Error("Prompt is required for image editing");
      }

      if (!imageUrls || !Array.isArray(imageUrls) || imageUrls.length === 0) {
        throw new Error("At least one image URL is required");
      }

      // Get model from params or fall back to store
      const {
        getSelectedEditModel,
        getCurrentModelParameters,
        getEditableModels,
        models,
      } = useImageModelStore.getState();

      let editModel;

      // If model is explicitly provided in params, use it
      if (requestedModelId && models[requestedModelId]) {
        editModel = models[requestedModelId];
        console.log("[ImageEditTool] Using requested model:", requestedModelId);
      } else {
        // Fall back to selectedEditModel from store
        editModel = getSelectedEditModel();
        console.log(
          "[ImageEditTool] Using store selectedEditModel:",
          editModel?.id
        );
      }

      // Validate model supports editing
      if (!editModel?.capabilities?.editing) {
        const editableModels = getEditableModels();
        console.warn(
          `Model ${editModel?.id || requestedModelId} doesn't support editing, falling back to gpt-image-1`
        );
        editModel =
          editableModels.find((m) => m.id === "gpt-image-1") ||
          editableModels.find((m) => m.id === "gemini-2.5-flash") ||
          editableModels[0];
      }

      if (!editModel) {
        throw new Error("No editing-capable model available");
      }

      console.log("[ImageEditTool] Final model for editing:", editModel.id);

      const modelParams = getCurrentModelParameters();

      // Apply rate limiting
      await rateLimit("image_editing", {
        maxRequests: 3,
        perMinute: true,
      });

      // Convert URLs to data or prepare for backend
      const image = imageUrls[0]; // Use first image as base
      const mask = maskUrl || undefined;

      // Edit image using the backend service
      // Always request b64_json - backend will download CDN URLs and convert to base64
      // This avoids CSP issues with fetching from external domains (fal.media, etc.)
      const result = await chatCompletionService.editImage({
        prompt,
        image,
        mask,
        model: editModel.id,
        provider: editModel.provider,
        signal: params.signal,
        ...modelParams,
        response_format: "b64_json",
      });

      console.log("[ImageEditTool] Edit result received:", {
        hasData: !!result?.data,
        dataLength: result?.data?.length,
        firstItemKeys: result?.data?.[0] ? Object.keys(result.data[0]) : [],
        provider: editModel.provider,
      });

      if (!result?.data || result.data.length === 0) {
        throw new Error("No image data returned from editing");
      }

      // Process all returned images and upload to Supabase
      const editedImages = [];
      // Collect has_nsfw_concepts from result if present
      const hasNsfwConcepts = result.has_nsfw_concepts || result.data?.[0]?.has_nsfw_concepts;

      for (const [index, imageData] of result.data.entries()) {
        console.log(`[ImageEditTool] Processing image ${index}:`, {
          hasB64: !!imageData.b64_json,
          hasUrl: !!imageData.url,
          provider: editModel.provider,
        });

        try {
          // Backend now provides b64_json for all providers (OpenAI, Gemini, FAL, etc.)
          // Primary path: Use b64_json if available
          // Fallback: Download from URL (may fail due to CSP for external CDNs)
          if (imageData.b64_json) {
            // Base64 from backend - upload directly to Supabase
            console.log(`[ImageEditTool] Uploading base64 image to Supabase`);
            const storageData = await uploadImageToStorage(
              imageData.b64_json,
              prompt
            );

            editedImages.push({
              url: storageData.url,
              index,
              storagePath: storageData.path,
              storageBucket: storageData.bucket,
              fileName: storageData.fileName,
            });
          } else if (imageData.url) {
            // Fallback: Backend didn't provide b64_json, try to download URL
            // Note: May fail for external CDN URLs due to CSP restrictions
            console.log(
              `[ImageEditTool] No b64_json, trying to download URL:`,
              imageData.url.substring(0, 100)
            );
            const storageData = await downloadAndUploadImage(
              imageData.url,
              prompt
            );

            editedImages.push({
              url: storageData.url,
              index,
              storagePath: storageData.path,
              storageBucket: storageData.bucket,
              fileName: storageData.fileName,
            });
          } else {
            console.warn(
              `[ImageEditTool] No valid image data for image ${index}`,
              imageData
            );
          }
        } catch (uploadError) {
          // Fallback to original URL if storage upload fails
          // Images will display but won't benefit from Supabase transformations
          console.error(
            `[ImageEditTool] Failed to save image ${index} to Supabase:`,
            uploadError.message
          );
          if (imageData.url) {
            editedImages.push({
              url: imageData.url,
              index,
              storagePath: null,
              storageBucket: null,
              fileName: null,
            });
          }
        }
      }

      return {
        success: true,
        result: {
          data: {
            images: editedImages,
            prompt,
            model: editModel.id,
            provider: editModel.provider,
            parameters: modelParams,
            originalImages: imageUrls,
            maskUsed: !!maskUrl,
            count: editedImages.length,
          },
          metadata: {
            timestamp: new Date().toISOString(),
            model: editModel.id,
            provider: editModel.provider,
            parameters: modelParams,
            prompt,
            format: "url",
            // Store original image URL for parent tracking in History tab
            original_image_url: imageUrls?.[0] || null,
            storageDetails: {
              bucket: "chat",
              isStoredInSupabase: true,
              imageCount: editedImages.length,
            },
            // Preserve has_nsfw_concepts from backend response if present
            ...(hasNsfwConcepts !== undefined ? { has_nsfw_concepts: hasNsfwConcepts } : {}),
          },
        },
      };
    } catch (error) {
      console.error("ImageEditTool execution failed:", error);
      return {
        success: false,
        error: error.message || "Image editing failed",
        errorType: "image_generation",
      };
    }
  }
}

// Legacy export for backward compatibility
export const editImage = async (params) => {
  const tool = new ImageEditTool();
  const result = await tool.executeImageEdit(params);

  if (result.success) {
    return {
      status: "success",
      result: result.result,
    };
  } else {
    return {
      status: "error",
      error: result.error,
      result: {
        metadata: {
          timestamp: new Date().toISOString(),
          prompt: params.prompt,
        },
      },
    };
  }
};

export const imageEditTool = new ImageEditTool();

export default imageEditTool;
